import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import '../../../assets/css/admin/dashboard/adminkyc/adminkyc.css';
import AdminMasterLayout from 'layouts/AdminMasterLayout';
import axios from 'axios';
import AppContext from 'context/AppContext';
import Swal from 'sweetalert2';

/* ─── Inline Icons ──────────────────────────────────────── */
const SearchIcon = () => <svg className="kyc-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>;
const FilterIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>;
const CloseIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;
const CheckIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>;
const EyeIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>;
const XIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;
const ChevDown = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>;
const ChevLeft = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>;
const ChevRight = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 6 15 12 9 18" /></svg>;
const BuildingIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9h1M14 9h1M9 14h1M14 14h1" /></svg>;
const PhoneIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 014.69 12 19.79 19.79 0 011.61 3.41 2 2 0 013.6 1.25h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L7.91 8.84a16 16 0 006.29 6.29l.96-.96a2 2 0 012.11-.45c.907.34 1.85.573 2.81.7A2 2 0 0122 16.92z" /></svg>;
const MapPinIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>;
const BadgeIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>;
const ClockIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;
const ChevUpIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="18 15 12 9 6 15" /></svg>;
const ExpandIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>;



/* ─── Success Popup ─────────────────────────────────────── */
const SuccessPopup = ({ show, message, onClose }) => {
  useEffect(() => {
    if (show) { const t = setTimeout(onClose, 2800); return () => clearTimeout(t); }
  }, [show]);
  if (!show) return null;
  return (
    <div className="kyc-toast">
      <div className="kyc-toast__icon"><CheckIcon /></div>
      <span>{message}</span>
    </div>
  );
};

/* ─── Document Image Lightbox ───────────────────────────── */
const Lightbox = ({ src, label, onClose }) => (
  <div className="kyc-lightbox" onClick={onClose}>
    <div className="kyc-lightbox__box" onClick={e => e.stopPropagation()}>
      <button className="kyc-lightbox__close" onClick={onClose}><CloseIcon /></button>
      <img src={src} alt={label} className="kyc-lightbox__img" />
      <p className="kyc-lightbox__label">{label}</p>
    </div>
  </div>
);

/* ─── KYC Detail Modal ──────────────────────────────────── */
const KYCModal = ({ record, onClose, onStatusChange }) => {
  const [reason, setReason] = useState('');
  const { baseUrlForFiles, loggedInSeller, baseUrl, authToken } = useContext(AppContext);
  const [docsOpen, setDocsOpen] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [confirmReject, setConfirmReject] = useState(false);

  useEffect(() => {
    setReason('');
    setConfirmReject(false);
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [record?.id]);
  const getDocumentLabel = (index) => {
    const labels = [
      'CNIC Front',
      'CNIC Back',
      'Selfie with CNIC',
      'Address Proof',
    ];

    return labels[index] || `Document ${index + 1}`;
  };

  if (!record) return null;

  const handleReject = () => {
    onStatusChange(record.id, 'Rejected');
    onClose();
  };

  const handleApprove = () => {
    onStatusChange(record.id, 'Approved');
    onClose();
  };

  return (
    <>
      <div className="kyc-modal-overlay" onClick={onClose}>
        <div className="kyc-modal" onClick={e => e.stopPropagation()}>

          {/* ── Modal Header ── */}
          <div className="kyc-modal__header">
            <div className="kyc-modal__user">
              <img
                src={`${baseUrlForFiles.replace(/\/$/, '')}/${record.exseller?.profileImage?.replace(/^\//, '')}`}
                alt={`${record.exseller?.firstName || ''} ${record.exseller?.lastName || ''}`}
                className="kyc-modal__avatar"
              />
              <div>
                <h3 className="kyc-modal__name">{record.exsellers?.firstName}{record.exsellers?.lastName}</h3>
                <p className="kyc-modal__email">{record.exsellers?.email}</p>
              </div>
            </div>
            <button className="kyc-modal__close" onClick={onClose}><CloseIcon /></button>
          </div>

          {/* ── Business title row ── */}
          <div className="kyc-modal__subtitle-row">
            <BuildingIcon />
            <span className="kyc-modal__biz-name">{record.businessName}</span>
            <span className="kyc-modal__sep">·</span>
            <span className="kyc-modal__biz-sub">{record.businessName}</span>
            <span className={`kyc-status-badge kyc-status-badge--${(record.status || 'pending').toLowerCase()}`}>
              {record.status}
            </span>
          </div>

          {/* ── Scrollable body ── */}
          <div className="kyc-modal__body">

            {/* Business Info */}
            <div className="kyc-modal__section">
              <h4 className="kyc-modal__section-title">Business Information</h4>
              <div className="kyc-modal__info-grid">
                <div className="kyc-modal__info-row">
                  <BadgeIcon />
                  <span className="kyc-modal__info-label">License Number</span>
                  <span className="kyc-modal__info-val">{record.licenseNumber}</span>
                </div>
                <div className="kyc-modal__info-row">
                  <ClockIcon />
                  <span className="kyc-modal__info-label">Experience</span>
                  <span className="kyc-modal__info-val">{record.experience}</span>
                </div>
                <div className="kyc-modal__info-row">
                  <MapPinIcon />
                  <span className="kyc-modal__info-label">Business Address</span>
                  <span className="kyc-modal__info-val">{record.businessAddress}</span>
                </div>
                <div className="kyc-modal__info-row">
                  <PhoneIcon />
                  <span className="kyc-modal__info-label">Business Phone</span>
                  <span className="kyc-modal__info-val">{record.businessPhone}</span>
                </div>
              </div>
            </div>

            {/* Uploaded Documents */}
            <div className="kyc-modal__section">
              <div className="kyc-modal__docs-header" onClick={() => setDocsOpen(o => !o)}>
                <h4 className="kyc-modal__section-title">Uploaded Documents</h4>
                <button className="kyc-modal__toggle-btn">
                  {docsOpen ? <ChevUpIcon /> : <ChevDown />}
                </button>
              </div>

              {docsOpen && (
                <div className="kyc-modal__docs-grid">

                  {record.exkycimages?.length > 0 ? (

                    record.exkycimages.map((doc, i) => {

                      const documentUrl = `${baseUrlForFiles.replace(/\/$/, '')}/${doc.document.replace(/^\//, '')}`;
                      const documentLabel = getDocumentLabel(i);

                      const isPdf =
                        doc.document?.toLowerCase().endsWith('.pdf');

                      return (
                        <div
                          key={doc.id || i}
                          className="kyc-modal__doc-card"
                        >

                          {/* Document Preview */}
                          <div
                            className="kyc-modal__doc-img-wrap"
                            onClick={() => {
                              if (!isPdf) {
                                setLightbox({
                                  img: documentUrl,
                                  label: documentLabel
                                });
                              } else {
                                window.open(documentUrl, '_blank');
                              }
                            }}
                            style={{ cursor: 'pointer' }}
                          >

                            {isPdf ? (

                              <div className="kyc-modal__pdf-preview">
                                <div className="kyc-modal__pdf-icon">
                                  📄
                                </div>

                                <span>PDF Document</span>

                                <small>
                                  Click to view
                                </small>
                              </div>

                            ) : (

                              <img
                                src={documentUrl}
                                alt={documentLabel}
                                className="kyc-modal__doc-img"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />

                            )}

                            {/* View Overlay */}
                            <div className="kyc-modal__doc-view">
                              <EyeIcon />
                              <span>
                                {isPdf ? 'Open PDF' : 'View Image'}
                              </span>
                            </div>

                          </div>

                          {/* Document Name */}
                          <p className="kyc-modal__doc-label">
                            {documentLabel}
                          </p>

                          {/* Original File Name */}
                          {/* <p className="kyc-modal__doc-file-name">
                            {doc.document?.split('/').pop()}
                          </p> */}

                        </div>
                      );
                    })

                  ) : (

                    <div className="kyc-modal__no-documents">
                      No documents uploaded.
                    </div>

                  )}

                </div>
              )}
            </div>
          </div>

          {/* ── Modal Footer ── */}
          <div className="kyc-modal__footer">
            <button
              className="kyc-modal__approve-btn"
              onClick={handleApprove}
              disabled={record.status === 'Approved'}
            >
              <CheckIcon /> Approve
            </button>

            <div className="kyc-modal__reject-wrap">
              <button
                className="kyc-modal__reject-btn"
                onClick={handleReject}
                disabled={record.status === 'Rejected'}
              >
                <XIcon /> Reject
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <Lightbox
          src={lightbox.img}
          label={lightbox.label}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
};

/* ─── Main Component ────────────────────────────────────── */
const AdminKyc = () => {
  const { baseUrl, baseUrlForFiles, loggedInSeller, authToken } = useContext(AppContext);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusTab, setStatusTab] = useState('All');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState({ show: false, message: '' });
  const [animRows, setAnimRows] = useState([]);
  const filterRef = useRef();
  const itemsPerPage = 5;


  const viewAllAdminKyc = () => {
    setLoading(true);
    axios.get(`${baseUrl}/admin/kyc/viewAllAdminKyc`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((response) => {
        console.log(response.data);
        if (response.data.success == true) {
          setRecords(response.data.kyc);
          setLoading(false);

        }
      }).catch((error) => {
        console.log(error);
        setLoading(false);

      })

  }
  useEffect(() => {
    viewAllAdminKyc();
  }, []);
  const updateKycStatus = (id, status) => {

    axios.post(
      `${baseUrl}/admin/kyc/updateKycStatus`,
      {
        id: id,
        status: status
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authToken}`
        }
      }
    )
      .then((response) => {

        console.log("KYC STATUS RESPONSE:", response.data);

        if (response.data.success) {

          setRecords(prev =>
            prev.map(item =>
              item.id === id
                ? {
                  ...item,
                  status: status
                }
                : item
            )
          );

          Swal.fire({
            title: 'Success!',
            text:
              status === 'Approved'
                ? 'KYC approved successfully!'
                : 'KYC rejected successfully!',
            icon: 'success',
            confirmButtonColor: '#000',
            timer: 2000,
            showConfirmButton: false
          });

          viewAllAdminKyc();

        } else {

          Swal.fire({
            title: 'Error!',
            text: response.data.message || 'KYC status update failed!',
            icon: 'error',
            confirmButtonColor: '#000'
          });

        }

      })
      .catch((error) => {

        console.log("KYC STATUS ERROR:", error);

        Swal.fire({
          title: 'Error!',
          text: error.response?.data?.message || 'Something went wrong!',
          icon: 'error',
          confirmButtonColor: '#000'
        });

      });
  };

  /* stagger row animations */
  useEffect(() => {
    setAnimRows([]);

    records.forEach((_, i) => {
      setTimeout(() => {
        setAnimRows(prev => [...prev, i]);
      }, i * 60);
    });

  }, [records]);

  /* close filter on outside click */
  useEffect(() => {
    const h = e => { if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  /* counts */
  const counts = {
    All: records.length,
    Pending: records.filter(r => r.status === 'Pending').length,
    Approved: records.filter(r => r.status === 'Approved').length,
    Rejected: records.filter(r => r.status === 'Rejected').length,
  };

  /* filtered list */
  const filtered = records.filter((r) => {
    const q = search.toLowerCase();

    const fullName =
      `${r.exsellers?.firstName || ''} ${r.exsellers?.lastName || ''}`.toLowerCase();

    const email =
      (r.exsellers?.email || '').toLowerCase();

    const business =
      (r.businessName || '').toLowerCase();

    const matchSearch =
      !q ||
      fullName.includes(q) ||
      email.includes(q) ||
      business.includes(q);

    const matchTab =
      statusTab === 'All' || r.status === statusTab;

    const matchFilter =
      filterStatus.length === 0 || filterStatus.includes(r.status);

    return matchSearch && matchTab && matchFilter;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);



  const handleInlineReject = (id) => {
    updateKycStatus(id, 'Rejected');
  };

  const toggleFilterStatus = (s) => {
    setFilterStatus(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
    );
    setCurrentPage(1);
  };

  const TABS = ['All', 'Pending', 'Approved', 'Rejected'];

  return (
    <AdminMasterLayout pageTitle='Kyc Verification'>
      <div className="kyc-page">

        {/* ── Page Header ── */}
        {/* <div className="kyc-header">
          <div>
            <h1 className="kyc-page-title">Admin KYC Verification</h1>
            <p className="kyc-page-sub">Review and verify KYC requests submitted by real estate businesses.</p>
          </div>
        </div> */}

        {/* ── Search + Tabs + Filter ── */}
        <div className="kyc-toolbar">
          <div className="kyc-toolbar__left">
            {/* Search */}
            <div className="kyc-search-wrap">
              <SearchIcon />
              <input
                className="kyc-search-input"
                type="text"
                placeholder="Search by name, email, or business"
                value={search}
                onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              />
              {search && (
                <button className="kyc-search-clear" onClick={() => setSearch('')}>
                  <CloseIcon />
                </button>
              )}
            </div>
          </div>
          {/* Status Tabs */}
          {/* <div className="kyc-tabs">
              {TABS.map(tab => (
                <button
                  key={tab}
                  className={`kyc-tab kyc-tab--${tab.toLowerCase()} ${statusTab === tab ? 'kyc-tab--active' : ''}`}
                  onClick={() => { setStatusTab(tab); setCurrentPage(1); }}
                >
                  {tab === 'Approved' && statusTab === tab && <CheckIcon />}
                  {tab} {counts[tab] > 0 && tab !== 'All' && (
                    <span className="kyc-tab-count">{counts[tab]}</span>
                  )}
                </button>
              ))}
            </div> */}


          {/* Filter dropdown */}
          <div className="kyc-filter-wrap" ref={filterRef}>
            {/* <button
              className={`kyc-filter-btn ${filterOpen ? 'kyc-filter-btn--open' : ''}`}
              onClick={() => setFilterOpen(o => !o)}
            >
              <FilterIcon /> Apply Filters <ChevDown />
            </button> */}
            {filterOpen && (
              <div className="kyc-filter-menu">
                <p className="kyc-filter-menu__label">Filter by Status</p>
                {['Pending', 'Approved', 'Rejected'].map(s => (
                  <label key={s} className="kyc-filter-menu__option">
                    <input
                      type="checkbox"
                      checked={filterStatus.includes(s)}
                      onChange={() => toggleFilterStatus(s)}
                      className="kyc-filter-checkbox"
                    />
                    <span className={`kyc-status-badge kyc-status-badge--${s.toLowerCase()}`}>{s}</span>
                  </label>
                ))}
                <button
                  className="kyc-filter-reset"
                  onClick={() => { setFilterStatus([]); setFilterOpen(false); }}
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Table ── */}
        <div className="kyc-table-card">
          <div className="kyc-table-wrap">
            <table className="kyc-table">
              <thead>
                <tr>
                  {/* <th>
                    <input type="checkbox" className="kyc-checkbox" />
                  </th> */}
                  <th>Seller</th>
                  <th>Business Name</th>
                  <th>Business Email</th>
                  <th>License Number</th>
                  <th>Business Phone</th>
                  <th>Business Address</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>

                {loading ? (

                  <tr>
                    <td colSpan="8">
                      <div className="kyc-table-loader">

                        <div className="kyc-table-loader-spinner"></div>

                        <h3>Loading KYC Records...</h3>

                        <p>Please wait while data is loading</p>

                      </div>
                    </td>
                  </tr>

                ) : paginated.length === 0 ? (

                  <tr>
                    <td colSpan="8" className="kyc-empty">
                      No records found.
                    </td>
                  </tr>

                ) : (

                  paginated.map((r, idx) => (
                    <tr
                      key={r.id}
                      className={`kyc-table-row ${animRows.includes(idx) ? 'kyc-table-row--visible' : ''}`}
                      style={{ animationDelay: `${idx * 0.06}s` }}
                    >

                      {/* Seller */}
                      <td>
                        <div className="kyc-seller-cell">
                          <img
                            src={`${baseUrlForFiles}${r.exseller?.profileImage}`}
                            alt={r.exseller?.fullName}
                            className="kyc-modal__avatar"
                          />
                          <div>
                            <p className="kyc-seller-name">
                              {r.exseller?.firstName} {r.exseller?.lastName}
                            </p>

                            <p className="kyc-seller-email">
                              {r.exseller?.email}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Business Name */}
                      <td className="kyc-td-biz">{r.businessName}</td>

                      {/* Email */}
                      <td className="kyc-td-email">{r.businessEmail}</td>

                      {/* license */}
                      <td className="kyc-td-email">{r.licenseNumber}</td>

                      {/* phone */}
                      <td className="kyc-td-email">{r.businessPhone}</td>

                      {/* address */}
                      <td className="kyc-td-email">{r.businessAddress}</td>

                      {/* Status */}
                      <td>
                        <span className={`kyc-status-badge kyc-status-badge--${(r.status || 'pending').toLowerCase()}`}>                          {r.status}
                        </span>
                      </td>

                      {/* Actions */}
                      <td>
                        <div className="kyc-actions">

                          <button
                            className="kyc-action-btn kyc-action-btn--view"
                            title="View Details"
                            onClick={() => setSelectedRecord(r)}
                          >
                            <EyeIcon />
                          </button>

                          <button
                            className="kyc-action-btn kyc-action-btn--reject"
                            title="Reject"
                            onClick={() => handleInlineReject(r.id)}
                            disabled={r.status === 'Rejected'}
                          >
                            <XIcon />
                          </button>

                        </div>
                      </td>

                    </tr>
                  ))

                )}

              </tbody>
            </table>
          </div>

          {/* ── Table Footer ── */}
          <div className="kyc-table-footer">
            <span className="kyc-entries">
              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filtered.length)}–{Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length} entries
            </span>
            <div className="kyc-pagination">
              <button className="kyc-page-btn" disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}>
                <ChevLeft />
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`kyc-page-num ${currentPage === i + 1 ? 'kyc-page-num--active' : ''}`}
                  onClick={() => setCurrentPage(i + 1)}
                >{i + 1}</button>
              ))}
              <button className="kyc-page-btn" disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => p + 1)}>
                <ChevRight />
              </button>
            </div>
          </div>
        </div>

        {/* ── KYC Detail Modal ── */}
        {selectedRecord && (
          <KYCModal
            record={records.find(r => r.id === selectedRecord.id) || selectedRecord}
            onClose={() => setSelectedRecord(null)}
            onStatusChange={updateKycStatus}
          />
        )}

        {/* ── Toast ── */}
        <SuccessPopup
          show={toast.show}
          message={toast.message}
          onClose={() => setToast({ show: false, message: '' })}
        />
      </div>
    </AdminMasterLayout>
  );
};

export default AdminKyc;